import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { storage } from '../config/firebase';
import { firestoreService } from '../services/firestoreService';
import { listAll, getMetadata, ref } from 'firebase/storage';
import { Loader, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface SyncProgress {
  fileName: string;
  status: 'pending' | 'syncing' | 'success' | 'error';
  error?: string;
}

const SyncFilesPage: React.FC = () => {
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncProgress[]>([]);
  const [completed, setCompleted] = useState(false);

  const handleSyncFiles = async () => {
    try {
      setSyncing(true);
      setCompleted(false);
      setSyncProgress([]);

      toast.loading('Scanning Firebase Storage...');

      // List all files in the transition folder recursively
      const allFiles: { path: string; name: string }[] = [];

      const listFilesRecursive = async (folderPath: string) => {
        try {
          const folderRef = ref(storage, folderPath);
          const result = await listAll(folderRef);

          // Add files from this folder
          for (const fileRef of result.items) {
            allFiles.push({
              path: fileRef.fullPath,
              name: fileRef.name,
            });
          }

          // Recursively list subdirectories
          for (const folderItem of result.prefixes) {
            await listFilesRecursive(folderItem.fullPath);
          }
        } catch (err) {
          console.error('Error listing folder:', folderPath, err);
        }
      };

      // Start from transition root
      await listFilesRecursive('transition');

      console.log('Found files:', allFiles);

      if (allFiles.length === 0) {
        toast.dismiss();
        toast.error('No files found in transition folder');
        return;
      }

      // Initialize progress for all files
      setSyncProgress(
        allFiles.map((f) => ({
          fileName: f.name,
          status: 'pending',
        }))
      );

      toast.dismiss();
      toast.loading(`Found ${allFiles.length} files. Syncing...`);

      let successCount = 0;
      let errorCount = 0;

      // Process each file
      for (let i = 0; i < allFiles.length; i++) {
        const file = allFiles[i];

        try {
          // Update progress to syncing
          setSyncProgress((prev) => {
            const updated = [...prev];
            updated[i].status = 'syncing';
            return updated;
          });

          // Extract year from path (e.g., "transition/2024/file.zip" -> 2024)
          const pathParts = file.path.split('/');
          if (pathParts.length < 3) {
            throw new Error('File not in year subdirectory');
          }

          const yearStr = pathParts[1];
          const year = parseInt(yearStr, 10);

          if (!year || year < 1900 || year > 2100) {
            throw new Error(`Invalid year: ${yearStr}`);
          }

          // Get file metadata from storage
          const fileRef = ref(storage, file.path);
          const metadata = await getMetadata(fileRef);

          // Check if file already exists in Firestore
          const existingFiles = await firestoreService.getFileMetadata(year);
          const alreadyExists = existingFiles.some((f) => f.fileName === file.name);

          if (!alreadyExists) {
            // Create Firestore document
            await firestoreService.createFileMetadata(
              file.name,
              year,
              metadata.size || 0,
              file.path
            );
          }

          successCount++;

          // Update progress to success
          setSyncProgress((prev) => {
            const updated = [...prev];
            updated[i].status = 'success';
            return updated;
          });
        } catch (err) {
          errorCount++;
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          console.error('Error processing file:', file.name, errorMessage);

          setSyncProgress((prev) => {
            const updated = [...prev];
            updated[i].status = 'error';
            updated[i].error = errorMessage;
            return updated;
          });
        }
      }

      toast.dismiss();
      setCompleted(true);
      toast.success(
        `Sync complete! ${successCount} files processed, ${errorCount} errors`
      );
    } catch (err) {
      toast.dismiss();
      const errorMessage = err instanceof Error ? err.message : 'Sync failed';
      console.error('Sync error:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Sync Files to Firestore
        </h1>
        <p className="text-gray-600">
          Scan Firebase Storage and populate the transition collection with metadata
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="mb-6">
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm text-blue-900">
                  This utility scans your Firebase Storage <code className="bg-blue-100 px-2 py-1 rounded">transition/</code> folder
                  and creates Firestore documents for all zip files found. It will:
                </p>
                <ul className="text-sm text-blue-900 mt-2 space-y-1 ml-4">
                  <li>• List all files in Storage</li>
                  <li>• Extract year from folder structure (transition/YYYY/filename)</li>
                  <li>• Get file size from Storage metadata</li>
                  <li>• Create entries in the transition Firestore collection</li>
                  <li>• Skip files that already exist in Firestore</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <Button
          onClick={handleSyncFiles}
          disabled={syncing}
          className="bg-indigo-600 hover:bg-indigo-700 text-white mb-6"
        >
          {syncing ? (
            <>
              <Loader className="mr-2 animate-spin" size={18} />
              Syncing...
            </>
          ) : (
            <>
              <CheckCircle className="mr-2" size={18} />
              Start Sync
            </>
          )}
        </Button>

        {syncProgress.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700 mb-3">
              Progress: {syncProgress.filter((p) => p.status === 'success').length}/{syncProgress.length}
            </p>
            <div className="space-y-1 max-h-96 overflow-y-auto border rounded p-3 bg-gray-50">
              {syncProgress.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm p-2 hover:bg-gray-100 rounded"
                >
                  <span className="font-mono text-gray-700 flex-1 truncate">
                    {item.fileName}
                  </span>
                  {item.status === 'pending' && (
                    <span className="text-xs text-gray-500 ml-2">Pending</span>
                  )}
                  {item.status === 'syncing' && (
                    <Loader className="animate-spin text-indigo-600 ml-2" size={16} />
                  )}
                  {item.status === 'success' && (
                    <CheckCircle className="text-green-600 ml-2" size={16} />
                  )}
                  {item.status === 'error' && (
                    <div title={item.error} className="ml-2">
                      <AlertCircle className="text-red-600" size={16} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {completed && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-4 mt-6">
            <div className="flex gap-3">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-green-900">Sync Complete!</p>
                <p className="text-sm text-green-700 mt-1">
                  Your transition collection has been populated with file metadata. You can now test the download page.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncFilesPage;
